import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { LuppApi } from '../lupp.service';
import { LUPP_QUERY_KEYS } from '../constants.lupp';
import {
  CustomerCase,
  UseGetCustomerCasesParams,
  CreateCustomerCaseRequest,
  CreateCustomerCaseData,
} from './dto.customer';

/*
|--------------------------------------------------------------------------
| GET CUSTOMER CASES
|--------------------------------------------------------------------------
*/

export const useGetCustomerCases = ({
  page = 0,
  size = 10,
  searchString,
  sort,
  enabled = true,
}: UseGetCustomerCasesParams = {}) => {
  return useQuery({
    queryKey: [LUPP_QUERY_KEYS.CUSTOMER_CASES, page, size, searchString, sort],
    queryFn: async () => {
      const queryParams = new URLSearchParams({
        page: String(page),
        size: String(size),
        sortDirection: 'DESC',
      });

      const body: { searchString?: string; sort?: string } = {};
      if (searchString) body.searchString = searchString;
      if (sort) body.sort = sort;

      const response = await LuppApi.post<
        { searchString?: string; sort?: string },
        {
          data: CustomerCase[];
          totalCount: number;
          pageNumber: number;
          pageSize: number;
          currentPageCount: number;
          totalPages: number;
        }
      >(`service/v1/customer-care/cases/searches?${queryParams.toString()}`, body, {
        extractData: (response) => response,
      });

      return {
        items: response.data,
        totalCount: response.totalCount,
        pageNumber: response.pageNumber,
        pageSize: response.pageSize,
        currentPageCount: response.currentPageCount,
        totalPages: response.totalPages,
      };
    },
    placeholderData: (previousData) => previousData,
    enabled,
  });
};

/*
|--------------------------------------------------------------------------
| GET CUSTOMER CASE BY ID
|--------------------------------------------------------------------------
*/

export const useGetCustomerCaseById = (caseId: string, enabled = true) => {
  return useQuery({
    queryKey: [LUPP_QUERY_KEYS.CUSTOMER_CASES, caseId],
    queryFn: async () => {
      const response = await LuppApi.get<CustomerCase>(`service/v1/customer-care/cases/${caseId}`);
      return response;
    },
    enabled: enabled && !!caseId,
  });
};

/*
|--------------------------------------------------------------------------
| CREATE CUSTOMER CASE
|--------------------------------------------------------------------------
*/

export const useCreateCustomerCase = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateCustomerCaseRequest) => {
      // LuppApi.post extracts response.data, so we get CreateCustomerCaseData directly
      const response = await LuppApi.post<CreateCustomerCaseRequest, CreateCustomerCaseData>(
        'service/v1/customer-care/cases',
        data
      );
      return response;
    },
    onSuccess: () => {
      // Invalidate and refetch customer cases list
      queryClient.invalidateQueries({ queryKey: [LUPP_QUERY_KEYS.CUSTOMER_CASES] });
    },
  });
};
